import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { url as API_BASE_URL } from '../../lib/apiConfig';
import toast from 'react-hot-toast';
import {
    IconSend, IconAlertCircle, IconCheck, IconLoader, IconRefresh,
} from '@tabler/icons-react';

const CATEGORIES = [
    { id: 'leadership',               label: 'Leadership',               description: 'Motivates the team, sets clear direction, and leads by example.' },
    { id: 'problem_solving',          label: 'Problem Solving',          description: 'Analytical thinking and ability to resolve complex issues effectively.' },
    { id: 'responsiveness',           label: 'Responsiveness',           description: 'Accessibility and speed of response to messages and requests.' },
    { id: 'interpersonal_dynamics',   label: 'Interpersonal Dynamics',   description: 'Communication style, inclusiveness, and team relationship management.' },
    { id: 'professionalism',          label: 'Professionalism',          description: 'Ethics, conduct, and maintaining high standards in interactions.' },
    { id: 'subject_matter_expertise', label: 'Subject Matter Expertise', description: 'Technical knowledge and depth of understanding in their field.' },
    { id: 'overall_assessment',       label: 'Overall Assessment',       description: 'Concise summary of performance and areas of impact.' },
];

// Plain axios — no auth interceptor for external token-based access
const publicHttp = axios.create({ baseURL: API_BASE_URL });

export default function Feedback360External() {
    const [searchParams] = useSearchParams();
    const token = searchParams.get('token') || '';

    const [nomination, setNomination]   = useState(null);
    const [loadingForm, setLoadingForm] = useState(true);
    const [loadError, setLoadError]     = useState('');

    const [comments, setComments]             = useState({});
    const [generalComment, setGeneralComment] = useState('');
    const [isSaving, setIsSaving]             = useState(false);
    const [isSubmitted, setIsSubmitted]       = useState(false);

    useEffect(() => {
        if (!token) {
            setLoadError('No token found. Please use the link from your email.');
            setLoadingForm(false);
            return;
        }
        publicHttp.get(`/pdp/360/external/?token=${token}`)
            .then(res => {
                setNomination(res.data);
                if (res.data.category_comments) {
                    const cats = {};
                    res.data.category_comments.forEach(c => { cats[c.category] = c.comment; });
                    setComments(cats);
                }
                setGeneralComment(res.data.general_comment || '');
                if (res.data.status === 'submitted') setIsSubmitted(true);
            })
            .catch(() => setLoadError('This feedback link is invalid or has expired.'))
            .finally(() => setLoadingForm(false));
    }, [token]);

    const filledCount = CATEGORIES.filter(cat => comments[cat.id]?.trim()).length;

    const handleSaveCategory = useCallback(async (categoryKey, value) => {
        if (isSubmitted || !value.trim()) return;
        setIsSaving(true);
        try {
            await publicHttp.post(`/pdp/360/external/?token=${token}`, {
                general_comment: generalComment,
                categories: [{ category: categoryKey, comment: value }],
            });
        } catch {
        } finally {
            setIsSaving(false);
        }
    }, [token, generalComment, isSubmitted]);

    const handleSubmit = async () => {
        if (filledCount < 7) {
            toast.error('Please complete all 7 categories before submitting.');
            return;
        }
        try {
            await publicHttp.post(`/pdp/360/external/submit/?token=${token}`, {
                general_comment: generalComment,
                categories: CATEGORIES.map(cat => ({ category: cat.id, comment: comments[cat.id] || '' })),
            });
            toast.success('Thank you! Your feedback has been submitted.');
            setIsSubmitted(true);
        } catch (err) {
            const missing = err?.response?.data?.missing_categories;
            if (missing?.length) {
                toast.error(`Missing: ${missing.join(', ')}`);
            } else {
                toast.error('Submission failed. Please try again.');
            }
        }
    };

    if (loadingForm) {
        return (
            <div className="min-h-screen bg-bg-light flex items-center justify-center">
                <div className="text-center text-text-secondary">
                    <IconLoader className="animate-spin inline-block mb-10" size={28} />
                    <p className="text-sm mt-10">Loading feedback form...</p>
                </div>
            </div>
        );
    }

    if (loadError) {
        return (
            <div className="min-h-screen bg-bg-light flex items-center justify-center p-20">
                <div className="bg-bg-dark rounded-lg border border-border shadow-medium p-32 max-w-md w-full text-center">
                    <div className="w-48 h-48 rounded-full bg-danger/10 flex items-center justify-center mx-auto mb-20">
                        <IconAlertCircle className="text-danger" size={24} />
                    </div>
                    <p className="text-[15px] font-medium text-text-primary uppercase tracking-widest mb-8">Link Invalid</p>
                    <p className="text-sm text-text-secondary">{loadError}</p>
                </div>
            </div>
        );
    }

    if (isSubmitted) {
        return (
            <div className="min-h-screen bg-bg-light flex items-center justify-center p-20">
                <div className="bg-bg-dark rounded-lg border border-border shadow-medium p-32 max-w-md w-full text-center">
                    <div className="w-52 h-52 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-20">
                        <IconCheck className="text-success" size={26} />
                    </div>
                    <p className="text-[15px] font-medium text-text-primary uppercase tracking-widest mb-8">Feedback Submitted!</p>
                    <p className="text-sm text-text-secondary">
                        Thank you for taking the time to provide feedback for{' '}
                        <strong>{nomination?.subject_name}</strong>. Your input is greatly appreciated.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-bg-light">
            {/* Top bar */}
            <div className="bg-bg-dark border-b border-border px-20 py-14">
                <div className="max-w-2xl mx-auto flex items-center justify-between">
                    <div className="flex items-center gap-12">
                        <div className="w-32 h-32 rounded-lg bg-primary flex items-center justify-center">
                            <IconRefresh size={16} className="text-white" />
                        </div>
                        <div>
                            <p className="text-sm font-bold text-text-primary">360° Feedback</p>
                            <p className="text-[10px] font-bold uppercase tracking-widest text-text-secondary">External Reviewer</p>
                        </div>
                    </div>
                    <div className="text-right">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-1">Progress</p>
                        <p className="text-sm font-bold text-text-primary">{filledCount}/7</p>
                    </div>
                </div>
            </div>

            <div className="max-w-2xl mx-auto px-20 py-30 pb-60">
                {/* Context card */}
                <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden mb-20">
                    <div className="p-20 border-b border-border bg-bg-light/30">
                        <h1 className="text-[15px] font-medium text-text-primary uppercase tracking-widest">
                            Feedback for <span className="text-primary">{nomination?.subject_name}</span>
                        </h1>
                        {nomination?.cycle_name && (
                            <p className="text-[10px] font-bold uppercase tracking-widest text-text-secondary opacity-70 mt-2">
                                {nomination.cycle_name}
                            </p>
                        )}
                    </div>
                    <div className="px-20 py-15">
                        <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest text-text-secondary mb-6">
                            <span>Completion Progress</span>
                            <span>{filledCount} / 7</span>
                        </div>
                        <div className="h-5 bg-bg-light rounded-full overflow-hidden">
                            <div className="h-full bg-primary transition-all duration-500" style={{ width: `${(filledCount / 7) * 100}%` }} />
                        </div>
                        <p className="text-[10px] text-text-secondary mt-8 italic">
                            {isSaving ? 'Saving...' : 'Your progress auto-saves as you go.'}
                        </p>
                    </div>
                </div>

                {/* Categories */}
                <div className="flex flex-col gap-15">
                    {CATEGORIES.map((cat, idx) => (
                        <div key={cat.id} className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
                            <div className="px-20 py-14 bg-bg-light/30 border-b border-border flex items-start justify-between">
                                <div className="flex items-start gap-12">
                                    <span className="w-22 h-22 rounded-full bg-bg-light border border-border flex items-center justify-center text-[10px] font-bold text-text-secondary flex-shrink-0 mt-1">
                                        {idx + 1}
                                    </span>
                                    <div>
                                        <h3 className="text-[13px] font-bold text-text-primary uppercase tracking-widest">{cat.label}</h3>
                                        <p className="text-[11px] text-text-secondary mt-2">{cat.description}</p>
                                    </div>
                                </div>
                                {comments[cat.id]?.trim() && <IconCheck size={14} className="text-success flex-shrink-0 mt-1" />}
                            </div>
                            <div className="p-20 form-group">
                                <textarea
                                    rows={4}
                                    placeholder={`Your feedback on ${cat.label.toLowerCase()}...`}
                                    value={comments[cat.id] || ''}
                                    onChange={e => setComments(prev => ({ ...prev, [cat.id]: e.target.value }))}
                                    onBlur={e => handleSaveCategory(cat.id, e.target.value)}
                                    style={{ resize: 'none' }}
                                />
                            </div>
                        </div>
                    ))}

                    {/* General comment */}
                    <div className="bg-bg-dark rounded-lg border border-border shadow-light overflow-hidden">
                        <div className="px-20 py-14 bg-bg-light/30 border-b border-border">
                            <h3 className="text-[13px] font-bold text-text-primary uppercase tracking-widest">General Comment</h3>
                            <p className="text-[11px] text-text-secondary mt-2">Optional — any additional overall thoughts.</p>
                        </div>
                        <div className="p-20 form-group">
                            <textarea
                                rows={3}
                                placeholder="Any other thoughts..."
                                value={generalComment}
                                onChange={e => setGeneralComment(e.target.value)}
                                onBlur={e => {
                                    if (e.target.value.trim()) {
                                        publicHttp.post(`/pdp/360/external/?token=${token}`, {
                                            general_comment: e.target.value, categories: [],
                                        }).catch(() => {});
                                    }
                                }}
                                style={{ resize: 'none' }}
                            />
                        </div>
                    </div>

                    {/* Submit */}
                    <div className="flex flex-col items-center gap-15 mt-10">
                        <div className="bg-warning/5 border border-warning/30 p-15 rounded-lg flex items-start gap-12 w-full">
                            <IconAlertCircle className="text-warning flex-shrink-0 mt-1" size={16} />
                            <p className="text-[11px] text-text-primary leading-relaxed">
                                <strong>Note:</strong> Once submitted your response cannot be edited.
                                All 7 categories must be filled before submitting.
                            </p>
                        </div>
                        <button
                            onClick={handleSubmit}
                            disabled={filledCount < 7}
                            className="btn btn-primary"
                        >
                            <IconSend size={15} />
                            Submit Feedback
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
